import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import "./App.css";
import FileDrop from "./components/FileDrop";
import MapView from "./components/MapView";
import ElevationChart, { type XAxis } from "./components/ElevationChart";
import LapTable from "./components/LapTable";
import { decodeFit, FitParseError } from "./fit/decode";
import { boundariesToSegments } from "./fit/laps";
import { encodeFit, validateFit } from "./fit/encode";
import type { RideData } from "./fit/types";
import { formatDistance, formatDuration, formatElevation, type Units } from "./units";

const MIN_GAP = 2; // records between adjacent lap boundaries
const MAX_HISTORY = 200;

interface Toast {
  kind: "ok" | "warn" | "error";
  text: string;
}

// --- Undo/redo history over the list of internal lap-boundary indices ---
interface Hist {
  past: number[][];
  present: number[];
  future: number[][];
  /** Ephemeral value shown during a drag; committed to history on drag end. */
  drag: number[] | null;
}
type HistAction =
  | { type: "init"; splits: number[] }
  | { type: "commit"; splits: number[] }
  | { type: "live"; splits: number[] }
  | { type: "commitDrag" }
  | { type: "undo" }
  | { type: "redo" };

const sameArr = (a: number[], b: number[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);
const pushPast = (past: number[][], v: number[]) =>
  [...past, v].slice(-MAX_HISTORY);

function histReducer(s: Hist, a: HistAction): Hist {
  switch (a.type) {
    case "init":
      return { past: [], present: a.splits, future: [], drag: null };
    case "commit":
      if (s.drag === null && sameArr(a.splits, s.present)) return s;
      return {
        past: pushPast(s.past, s.present),
        present: a.splits,
        future: [],
        drag: null,
      };
    case "live":
      return { ...s, drag: a.splits };
    case "commitDrag":
      if (s.drag === null) return s;
      if (sameArr(s.drag, s.present)) return { ...s, drag: null };
      return {
        past: pushPast(s.past, s.present),
        present: s.drag,
        future: [],
        drag: null,
      };
    case "undo": {
      if (s.past.length === 0) return s;
      const present = s.past[s.past.length - 1];
      return {
        past: s.past.slice(0, -1),
        present,
        future: [s.present, ...s.future],
        drag: null,
      };
    }
    case "redo": {
      if (s.future.length === 0) return s;
      const [present, ...rest] = s.future;
      return { past: pushPast(s.past, s.present), present, future: rest, drag: null };
    }
  }
}

interface FocusRange {
  startIdx: number;
  endIdx: number;
  key: number;
}

export default function App() {
  const [ride, setRide] = useState<RideData | null>(null);
  const [hist, dispatch] = useReducer(histReducer, {
    past: [],
    present: [],
    future: [],
    drag: null,
  });
  const splits = hist.drag ?? hist.present;
  const splitsRef = useRef(splits);
  splitsRef.current = splits;
  const canUndo = hist.past.length > 0;
  const canRedo = hist.future.length > 0;

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverLap, setHoverLap] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [focusRange, setFocusRange] = useState<FocusRange | null>(null);
  const [units, setUnits] = useState<Units>("metric");
  const [xAxis, setXAxis] = useState<XAxis>("distance");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const handleFile = useCallback(async (file: File) => {
    setBusy(true);
    setError(null);
    setToast(null);
    try {
      const buf = await file.arrayBuffer();
      const data = decodeFit(file.name, buf);
      setRide(data);
      dispatch({ type: "init", splits: data.initialSplits });
      setSelected(null);
      setHoverIndex(null);
      setFocusRange(null);
      if (!data.hasPosition) {
        setToast({
          kind: "warn",
          text: "This file has no GPS positions, so the map will be empty. You can still edit laps on the elevation graph.",
        });
      }
    } catch (err) {
      const msg =
        err instanceof FitParseError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not read that file.";
      setError(msg);
      setRide(null);
    } finally {
      setBusy(false);
    }
  }, []);

  const lastIndex = ride ? ride.records.length - 1 : 0;

  const addSplit = useCallback(
    (idx: number) => {
      if (!ride) return;
      const prev = splitsRef.current;
      if (
        idx < MIN_GAP ||
        idx > lastIndex - MIN_GAP ||
        prev.some((s) => Math.abs(s - idx) < MIN_GAP)
      ) {
        return;
      }
      const next = [...prev, idx].sort((a, b) => a - b);
      dispatch({ type: "commit", splits: next });
      setSelected(next.indexOf(idx));
    },
    [ride, lastIndex],
  );

  const moveSplitAt = useCallback((pos: number, idx: number) => {
    const base = splitsRef.current;
    if (base[pos] === idx) return;
    const next = [...base];
    next[pos] = idx;
    dispatch({ type: "live", splits: next });
  }, []);

  const commitMove = useCallback(() => dispatch({ type: "commitDrag" }), []);

  const removeSplitAt = useCallback((pos: number) => {
    const next = splitsRef.current.filter((_, i) => i !== pos);
    dispatch({ type: "commit", splits: next });
    setSelected(null);
  }, []);

  const resetLaps = useCallback(() => {
    if (ride) dispatch({ type: "commit", splits: ride.initialSplits });
    setSelected(null);
  }, [ride]);

  const clearLaps = useCallback(() => {
    dispatch({ type: "commit", splits: [] });
    setSelected(null);
  }, []);

  const undo = useCallback(() => {
    dispatch({ type: "undo" });
    setSelected(null);
  }, []);
  const redo = useCallback(() => {
    dispatch({ type: "redo" });
    setSelected(null);
  }, []);

  const loadDifferent = useCallback(() => {
    setRide(null);
    dispatch({ type: "init", splits: [] });
    setError(null);
    setToast(null);
  }, []);

  // Keyboard: Ctrl/Cmd+Z undo, Ctrl/Cmd+Y or Cmd+Shift+Z redo,
  // Delete/Backspace removes the selected boundary.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!ride) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redo();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selected !== null) {
        e.preventDefault();
        removeSplitAt(selected);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ride, selected, removeSplitAt, undo, redo]);

  const segments = useMemo(
    () => (ride ? boundariesToSegments(ride.records, splits) : []),
    [ride, splits],
  );

  // The lap segment to highlight: an explicitly hovered table row wins,
  // otherwise the lap containing the record currently hovered on the
  // map/graph. Highlights the trace on the map and the band in the graph.
  const highlight = useMemo(() => {
    if (segments.length === 0) return null;
    let lap = hoverLap;
    if (lap === null && hoverIndex !== null) {
      const found = segments.findIndex(
        (s) => hoverIndex >= s.startIdx && hoverIndex <= s.endIdx,
      );
      lap = found < 0 ? null : found;
    }
    if (lap === null || !segments[lap]) return null;
    return {
      lap,
      startIdx: segments[lap].startIdx,
      endIdx: segments[lap].endIdx,
    };
  }, [segments, hoverLap, hoverIndex]);

  // Zoom the map to a lap when its table row is clicked.
  const focusLap = useCallback(
    (lap: number) => {
      const seg = segments[lap];
      if (!seg) return;
      setFocusRange({
        startIdx: seg.startIdx,
        endIdx: seg.endIdx,
        key: Date.now(),
      });
    },
    [segments],
  );

  // Human-readable readout for a record (used by the drag tooltips).
  const describeIndex = useCallback(
    (i: number) => {
      if (!ride) return "";
      const r = ride.records[i];
      if (!r) return "";
      const parts = [formatDuration((r.time - ride.records[0].time) / 1000)];
      if (r.distance !== null) parts.push(formatDistance(r.distance, units));
      if (r.altitude !== null) parts.push(formatElevation(r.altitude, units));
      return parts.join(" · ");
    },
    [ride, units],
  );

  const download = useCallback(() => {
    if (!ride) return;
    setToast(null);
    try {
      const bytes = encodeFit(ride, splits);
      const result = validateFit(bytes, segments.length);
      // Copy into a fresh ArrayBuffer-backed Uint8Array for the Blob.
      const blob = new Blob([bytes.slice()], {
        type: "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const base = ride.fileName.replace(/\.fit$/i, "");
      a.href = url;
      a.download = `${base}_with_laps.fit`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setToast(
        result.ok
          ? {
              kind: "ok",
              text: `Downloaded ${segments.length} lap${segments.length === 1 ? "" : "s"} · validated (${result.recordCount} records preserved).`,
            }
          : {
              kind: "warn",
              text: `Downloaded, but validation warned: ${result.message}`,
            },
      );
    } catch (err) {
      setToast({
        kind: "error",
        text: `Export failed: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  }, [ride, splits, segments.length]);

  if (!ride) {
    return (
      <div className="landing">
        <header className="landing-head">
          <span className="logo">Flapper</span>
          <span className="logo-sub">FIT lap editor</span>
        </header>
        <div className="landing-body">
          <h1 className="landing-title">Add and edit laps in your .fit files</h1>
          <p className="landing-lead">
            Drop in a ride, see it on a map and elevation profile, then click to
            add lap boundaries or drag and remove existing ones. Download a new
            .fit file when you're done.
          </p>
          <FileDrop onFile={handleFile} error={error} busy={busy} />
        </div>
        <footer className="landing-foot">
          Runs entirely in your browser · nothing is uploaded.
        </footer>
      </div>
    );
  }

  return (
    <div className="editor">
      <header className="toolbar">
        <div className="toolbar-left">
          <span className="logo small">Flapper</span>
          <span className="file-name" title={ride.fileName}>
            {ride.fileName}
          </span>
          <span className="lap-count">
            {segments.length} lap{segments.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="toolbar-right">
          <div className="toggle" role="group" aria-label="History">
            <button
              onClick={undo}
              disabled={!canUndo}
              title="Undo (Ctrl/⌘+Z)"
              aria-label="Undo"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <path
                  fill="currentColor"
                  d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"
                />
              </svg>
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              title="Redo (Ctrl/⌘+Y)"
              aria-label="Redo"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
                <path
                  fill="currentColor"
                  d="M18.4 10.6C16.55 8.99 14.15 8 11.5 8c-4.65 0-8.58 3.03-9.96 7.22L3.9 16c1.05-3.19 4.05-5.5 7.6-5.5 1.96 0 3.73.72 5.12 1.88L13 16h9V7l-3.6 3.6z"
                />
              </svg>
            </button>
          </div>
          <div className="toggle" role="group" aria-label="X axis">
            <button
              className={xAxis === "distance" ? "on" : ""}
              onClick={() => setXAxis("distance")}
            >
              Distance
            </button>
            <button
              className={xAxis === "time" ? "on" : ""}
              onClick={() => setXAxis("time")}
            >
              Time
            </button>
          </div>
          <div className="toggle" role="group" aria-label="Units">
            <button
              className={units === "metric" ? "on" : ""}
              onClick={() => setUnits("metric")}
            >
              km
            </button>
            <button
              className={units === "imperial" ? "on" : ""}
              onClick={() => setUnits("imperial")}
            >
              mi
            </button>
          </div>
          <button className="ghost" onClick={resetLaps}>
            Reset
          </button>
          <button className="ghost" onClick={clearLaps}>
            Clear laps
          </button>
          <button className="ghost" onClick={loadDifferent}>
            New file
          </button>
          <button className="primary" onClick={download}>
            Download .fit
          </button>
        </div>
      </header>

      {toast && <div className={`toast ${toast.kind}`}>{toast.text}</div>}

      <p className="hint-bar">
        <span className="hint-desktop">
          Click the map or graph to add a lap · drag a marker to move it · use
          the ✕ or “merge ↓” to remove one · click a lap row to zoom the map to
          it.
        </span>
        <span className="hint-mobile">
          Slide the cursor on the graph and tap <b>Add lap here</b> · tap a
          marker to select it, then nudge, move, or remove it.
        </span>
      </p>

      <div className="panes">
        <div className="map-pane">
          <MapView
            records={ride.records}
            splits={splits}
            hoverIndex={hoverIndex}
            selected={selected}
            highlight={highlight}
            focusRange={focusRange}
            describeIndex={describeIndex}
            onHover={setHoverIndex}
            onAddSplit={addSplit}
            onMoveSplitAt={moveSplitAt}
            onCommitMove={commitMove}
            onRemoveSplitAt={removeSplitAt}
            onSelect={setSelected}
          />
        </div>
        <div className="chart-pane">
          <ElevationChart
            records={ride.records}
            splits={splits}
            hoverIndex={hoverIndex}
            selected={selected}
            highlight={highlight}
            units={units}
            xAxis={xAxis}
            describeIndex={describeIndex}
            onHover={setHoverIndex}
            onAddSplit={addSplit}
            onMoveSplitAt={moveSplitAt}
            onCommitMove={commitMove}
            onRemoveSplitAt={removeSplitAt}
            onSelect={setSelected}
          />
        </div>
      </div>

      <LapTable
        segments={segments}
        units={units}
        highlightLap={highlight?.lap ?? null}
        onHoverLap={setHoverLap}
        onFocusLap={focusLap}
        onRemoveSplitAt={removeSplitAt}
      />

      <p className="disclaimer">
        Lap summaries are written with start/end, distance and times; detailed
        metrics (power, HR, ascent, etc.) are left for Garmin Connect / Strava to
        recompute on import. The output is round-trip validated before download.
      </p>
    </div>
  );
}
